import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost, formatDateTime } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Database, Download, Loader2, CheckCircle2, AlertTriangle, ListChecks, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, tone }) => {
  const cls =
    tone === "success" ? "text-[hsl(var(--success))]"
    : tone === "warning" ? "text-[hsl(var(--warning))]"
    : tone === "muted" ? "text-muted-foreground"
    : "text-foreground";
  return (
    <div className="rounded-md border bg-card px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
};

const CseImportCard = () => {
  const [lastRun, setLastRun] = useState(null);
  const [loadingLast, setLoadingLast] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadLastRun = () => {
    setLoadingLast(true);
    apiGet("/admin/cse-import/last-run")
      .then((r) => setLastRun(r.last_run || null))
      .catch(() => setLastRun(null))
      .finally(() => setLoadingLast(false));
  };

  useEffect(loadLastRun, []);

  const previewImport = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const res = await apiPost("/admin/cse-import/preview", {});
      if (!res.ok) {
        toast.error(res.error || "Preview failed");
        return;
      }
      setPreview(res);
      setOpenConfirm(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  const runImport = async () => {
    setRunning(true);
    try {
      const res = await apiPost("/admin/cse-import/run", {});
      if (!res.ok) {
        toast.error(res.error || "Import failed");
        return;
      }
      toast.success(
        `Imported ${res.companies_inserted ?? 0} companies, ${res.securities_inserted ?? 0} securities, ${res.prices_inserted ?? 0} prices.`,
      );
      setLastResult(res);
      setOpenConfirm(false);
      setPreview(null);
      loadLastRun();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setRunning(false); }
  };

  return (
    <>
      <Card className="border bg-card" data-testid="cse-import-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            CSE Bulk Import
          </CardTitle>
          <CardDescription>
            Expand the tracked universe to <strong>all ~290</strong> CSE-listed
            securities by importing from the latest market snapshot. Preview
            first, then commit when you're ready. Re-running is safe — only
            new entries are inserted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {lastResult && (
            <div
              className="rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-4 py-3 text-xs space-y-2"
              data-testid="cse-import-success-banner"
            >
              <div className="flex items-center gap-2 font-semibold text-[hsl(var(--success))] text-sm">
                <CheckCircle2 className="h-4 w-4" />
                Import complete
              </div>
              <ul className="text-foreground/80 leading-relaxed pl-1 space-y-0.5">
                <li>{lastResult.companies_inserted} new companies added</li>
                <li>{lastResult.securities_inserted} new securities added</li>
                <li>{lastResult.prices_inserted} new daily prices added</li>
                <li>{lastResult.companies_existing + lastResult.securities_existing} already existed (skipped safely)</li>
                <li>{lastResult.skipped_unsupported_suffix} rights / warrants skipped</li>
              </ul>
              <div className="flex items-center gap-2 pt-1">
                <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="cse-import-view-screener">
                  <Link to="/stocks">
                    View all stocks in Screener
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLastResult(null)}
                  data-testid="cse-import-dismiss-success"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          {loadingLast ? (
            <div className="text-xs text-muted-foreground">Loading last run…</div>
          ) : lastRun ? (
            <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-xs space-y-1" data-testid="cse-import-last-run">
              <div className="flex items-center gap-2 font-medium text-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                Last import: {formatDateTime(lastRun.run_at)}
              </div>
              <div className="text-muted-foreground">
                Snapshot: {lastRun.snapshot_date?.slice(0, 10)} —{" "}
                {lastRun.companies_inserted} companies, {lastRun.securities_inserted} securities, {lastRun.prices_inserted} prices.
              </div>
            </div>
          ) : (
            <div className="rounded-md border bg-[hsl(var(--info))]/5 px-3 py-2.5 text-xs text-muted-foreground" data-testid="cse-import-no-runs">
              <span className="inline-flex items-center gap-1.5">
                <ListChecks className="h-3.5 w-3.5" />
                No imports run yet. Click <strong>Preview Import</strong> to see what would be added.
              </span>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={previewImport}
              disabled={previewing || running}
              variant="outline"
              className="gap-2"
              data-testid="cse-import-preview-btn"
            >
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ListChecks className="h-4 w-4" />}
              Preview Import
            </Button>
            <Button
              onClick={async () => { await previewImport(); }}
              disabled={previewing || running}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="cse-import-run-btn"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Import All CSE Stocks
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Both buttons start by computing a dry-run preview from the latest <code>cse_market_snapshot</code>.
            Nothing is written until you confirm.
          </p>
        </CardContent>
      </Card>

      <Dialog open={openConfirm} onOpenChange={(o) => !o && !running && setOpenConfirm(false)}>
        <DialogContent
          className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
          data-testid="cse-import-confirm-dialog"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              Confirm CSE Bulk Import
            </DialogTitle>
            <DialogDescription>
              This is a <strong>preview</strong> of the changes that will be applied. No data has been written yet.
              Re-running is safe — already-imported rows are skipped.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] border-[hsl(var(--info))]/40 text-[hsl(var(--info))]">
                    Snapshot {preview.snapshot_date?.slice(0, 10)}
                  </Badge>
                  <span className="text-muted-foreground">
                    {preview.snapshot_total_rows} rows total · skipped {preview.skipped_invalid + preview.skipped_unsupported_suffix} (rights / warrants / malformed)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <Stat label="Companies — new" value={preview.companies_new} tone="success" />
                <Stat label="Companies — existing" value={preview.companies_existing} tone="muted" />
                <Stat label="—" value="" tone="muted" />
                <Stat label="Securities — new" value={preview.securities_new} tone="success" />
                <Stat label="Securities — existing" value={preview.securities_existing} tone="muted" />
                <Stat label="—" value="" tone="muted" />
                <Stat label="Daily prices — new" value={preview.prices_new} tone="success" />
                <Stat label="Daily prices — existing" value={preview.prices_existing} tone="muted" />
                <Stat label="—" value="" tone="muted" />
              </div>

              {preview.companies_new === 0 && preview.securities_new === 0 && preview.prices_new === 0 && (
                <div className="flex items-start gap-2 rounded-md border bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] px-3 py-2 text-xs">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                  <span>Nothing new to import. Everything from this snapshot is already in the database.</span>
                </div>
              )}

              {Object.keys(preview.sectors_breakdown || {}).length > 0 && (
                <div className="rounded-md border bg-card px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                    New companies by sector
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(preview.sectors_breakdown)
                      .sort((a, b) => b[1] - a[1])
                      .map(([sector, count]) => (
                        <Badge
                          key={sector}
                          variant="outline"
                          className={`text-[10px] ${sector === "Unclassified" ? "bg-muted text-muted-foreground" : ""}`}
                        >
                          {sector}: {count}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}

              {(preview.sample_new_companies || []).length > 0 && (
                <div className="rounded-md border bg-card">
                  <div className="px-3 py-2 border-b bg-muted/30 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Sample of new companies (first 10)
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b">
                        <th className="px-3 py-1.5 font-medium">Code</th>
                        <th className="px-3 py-1.5 font-medium">Name</th>
                        <th className="px-3 py-1.5 font-medium text-right">Sector</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample_new_companies.map((c) => (
                        <tr key={c.company_code} className="border-b last:border-0">
                          <td className="px-3 py-1.5 font-mono">{c.company_code}</td>
                          <td className="px-3 py-1.5">{c.company_name}</td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">{c.sector}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenConfirm(false)}
              disabled={running}
              data-testid="cse-import-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={runImport}
              disabled={
                running ||
                !preview ||
                (preview.companies_new === 0 && preview.securities_new === 0 && preview.prices_new === 0)
              }
              className="gap-2"
              data-testid="cse-import-confirm"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {running ? "Importing…" : "Run import now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CseImportCard;
